import { convertPrices } from '../utils/storefront'
import { sendKlevuAnalytics } from '../utils/klevu'
import { formatMoney } from '../utils/shopify'

export default (reducedFilters = '') => ({
    searchTerm: Alpine.$persist('searchResults').using(sessionStorage),
    products: Alpine.$persist([])
        .as('searchResults-products')
        .using(sessionStorage),
    totalProducts: Alpine.$persist(0)
        .as('searchResults-totalProducts')
        .using(sessionStorage),
    limit: 16,
    offset: Alpine.$persist(0).as('searchResults-offset').using(sessionStorage),
    filters: Alpine.$persist([])
        .as('searchResults-filters')
        .using(sessionStorage),
    filtersOpen: false,
    activeFilters: Alpine.$persist([])
        .as('searchResults-activeFilters')
        .using(sessionStorage),
    minPriceLimit: Alpine.$persist(null)
        .as('searchResults-minPriceLimit')
        .using(sessionStorage),
    minPrice: Alpine.$persist(null)
        .as('searchResults-minPrice')
        .using(sessionStorage),
    minPriceLabel: Alpine.$persist('')
        .as('searchResults-minPriceLabel')
        .using(sessionStorage),
    maxPriceLimit: Alpine.$persist(null)
        .as('searchResults-maxPriceLimit')
        .using(sessionStorage),
    maxPrice: Alpine.$persist(null)
        .as('searchResults-maxPrice')
        .using(sessionStorage),
    maxPriceLabel: Alpine.$persist('')
        .as('searchResults-maxPriceLabel')
        .using(sessionStorage),
    minThumb: Alpine.$persist(0)
        .as('searchResults-minThumb')
        .using(sessionStorage),
    maxThumb: Alpine.$persist(0)
        .as('searchResults-maxThumb')
        .using(sessionStorage),
    buffer: Alpine.$persist(0).as('searchResults-buffer').using(sessionStorage),
    sortOptions: [
        {
            label: 'Most Relevant',
            value: 'RELEVANCE',
        },
        {
            label: 'Most Recent',
            value: 'NEW_ARRIVAL_DESC',
        },
        {
            label: 'Price: Low to High',
            value: 'PRICE_ASC',
        },
        {
            label: 'Price: High to Low',
            value: 'PRICE_DESC',
        },
    ],
    activeSort: Alpine.$persist('RELEVANCE')
        .as('searchResults-activeSort')
        .using(sessionStorage),
    activeSortLabel: Alpine.$persist('Most Relevant')
        .as('searchResults-activeSortLabel')
        .using(sessionStorage),
    loading: false,
    loadMoreLoading: false,
    loadMoreText: 'Load More',
    currency: {
        ...shopifyData.currency,
        code: Alpine.$persist('')
            .as('searchResults-currencyCode')
            .using(sessionStorage),
    },
    columnSize: Alpine.$persist(2)
        .as('searchResults-columnSize')
        .using(sessionStorage),

    init() {
        if (!this.currency.code) this.currency.code = this.currency.iso_code

        const searchParams = new URLSearchParams(window.location.search)
        const queryTerm = searchParams.get('q')
        const navigatedFromExternalUrl = searchParams.has('curatededit_email')

        if (
            queryTerm !== this.searchTerm ||
            this.currency.code !== shopifyData.currency.iso_code
        ) {
            this.resetData()
            this.currency.code = shopifyData.currency.iso_code

            if (queryTerm) {
                this.searchTerm = queryTerm
            } else {
                // Resetting the search param due to currency switcher
                // removing the search parameter
                searchParams.set('q', this.searchTerm)
                window.history.replaceState(
                    {},
                    '',
                    `${window.location.pathname}?${searchParams.toString()}`
                )
            }

            const filters = reducedFilters ? reducedFilters.split(',') : []
            const request = this.createRequest(true, filters)
            this.$nextTick(() => this.fetchProducts(request, 'loading', true))
        }

        this.$watch(
            'loadMoreLoading',
            (value) => (this.loadMoreText = value ? 'Loading…' : 'Load more')
        )

        this.$watch('filters', () => {
            this.updateActiveFilters()
            this.fetchFilteredSortedProducts()
        })

        this.$watch('activeSort', (value) => {
            this.setActiveSortLabel(value)
            this.fetchFilteredSortedProducts()
        })

        if (navigatedFromExternalUrl) {
            this.$nextTick(() => {
                const apiKey = shopifyData.klevuApi.key
                const analyticsUrl = [
                    `https://stats.ksearchnet.com/analytics/n-search/search`,
                    `?apiKey=${apiKey}`,
                    `&term=${encodeURIComponent(this.searchTerm)}`,
                    `&klevu_totalResults=${this.totalProductsFound}`,
                    `&klevu_typeOfQuery=FUZZY_AND`,
                ].join('')

                fetch(analyticsUrl, {
                    method: 'GET',
                    headers: {},
                })
            })
        }

        const sort = document.getElementById('products-sort')
        if (sort) {
            window.addEventListener('load', () => {
                this.updateSelectWidth(sort)
            })
        }
    },

    resetData() {
        this.filters = []
        this.activeFilters = []
        this.activeSort = 'RELEVANCE'
        this.products = []
        this.offset = 0
        this.minPriceLimit = 0
        this.minPrice = 0
        this.minPriceLabel = ''
        this.maxPriceLimit = 0
        this.maxPrice = 0
        this.maxPriceLabel = ''
        this.minThumb = 0
        this.maxThumb = 0
        this.buffer = 0
    },
    createRequest(returnFilters = false, filtersToReturn = []) {
        const request = {
            recordQueries: [
                {
                    id: 'productSearch',
                    typeOfQuery: 'SEARCH',
                    settings: {
                        typeOfSearch: 'FUZZY_AND',
                        query: {
                            term: this.searchTerm,
                        },
                        sort: this.activeSort,
                        limit: this.limit,
                        offset: this.offset || 0,
                        fields: [
                            'id',
                            'itemGroupId',
                            'name',
                            'price',
                            'startPrice',
                            'salePrice',
                            'url',
                            'image',
                            'imageHover',
                            'additionalDataToReturn',
                            'tag'
                        ],
                        typeOfRecords: ['KLEVU_PRODUCT'],
                    },
                },
            ],
            context: {
                apiKeys: [shopifyData.klevuApi.key],
            },
        }

        const record = request.recordQueries[0]

        if (returnFilters) {
            record.filters = {
                filtersToReturn: {
                    enabled: true,
                    options: {
                        limit: 100,
                    },
                },
            }

            if (filtersToReturn.length) {
                record.filters.filtersToReturn.include = filtersToReturn
            }
        }

        const activeFilters = this.filters.filter((filter) => {
            return filter.options.some((option) => option.selected)
        })

        if (activeFilters.length) {
            const filtersToApply = activeFilters.map((filter) => {
                const values = filter.options
                    .filter((option) => option.selected)
                    .map((option) => option.value)
                return {
                    key: filter.key,
                    values: values,
                    settings: {
                        singleSelect: false,
                    },
                }
            })

            record.filters = record.filters || {}

            record.filters.applyFilters = {
                filters: [...filtersToApply],
            }
        }

        return request
    },
    fetchProducts(request, loadingKey = 'loading', initialFetch = false) {
        this[loadingKey] = true

        fetch(shopifyData.klevuApi.searchUrl, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
            },
            body: JSON.stringify(request),
        })
            .then((response) => response.json())
            .then(async (data) => {
                if (initialFetch) {
                    this.setFilters(data)
                }

                let products = data.queryResults[0].records

                products = products.map((product) => {
                    const url = new URL(product.url)

                    product.test = 'test'
                    product.url = url.pathname
                    product.price = product.price * 100
                    product.startPrice = product.startPrice * 100
                    product.salePrice = product.salePrice * 100
                    product.imageUrl = product.imageUrl.replace('_medium', '')
                    product.imageHover = product.imageHover.replace('_medium', '')
                    product.variants = JSON.parse(product.additionalDataToReturn)?.variants || []

                    return product
                })

                await convertPrices(products).then((convertedProducts) => {
                    if (initialFetch || !this.offset) {
                        this.products = convertedProducts
                    } else {
                        this.products.push(...convertedProducts)
                    }

                    if (initialFetch || !this.offset) {
                        this.totalProductsFound =
                            data.queryResults[0].meta.totalProductsFound
                    }

                    this[loadingKey] = false
                })
            })
            .catch((err) => {
                console.error(err)
                this[loadingKey] = false
            })
    },
    fetchFilteredSortedProducts() {
        this.offset = 0

        const request = this.createRequest()

        this.fetchProducts(request)
    },
    loadMore() {
        this.offset += this.limit

        const request = this.createRequest()
        this.fetchProducts(request, 'loadMoreLoading')
    },
    setFilters(data) {
        this.filters = this.transformFilters(data.queryResults[0].filters)

        const brandFilter = this.filters.find(
            (filter) => filter.key === 'brand'
        )
        if (brandFilter && brandFilter.options.length <= 1) {
            this.filters = this.filters.filter(
                (filter) => filter.key !== 'brand'
            )
        }

        this.sortSizeFilter()

        this.updateActiveFilters()

        const priceFilter = this.filters.find(
            (filter) => filter.key === 'price'
        )
        if (priceFilter && this.currency.code === this.currency.default) {
            this.initPriceFilter(priceFilter)
        } else {
            this.filters = this.filters.filter(
                (filter) => filter.key !== 'price'
            )
        }
    },
    sortSizeFilter() {
        const sizeFilter = this.filters.find((filter) => filter.key === 'size')

        if (sizeFilter) {
            const orderedOptions = []

            const stringOptions = sizeFilter.options.filter(
                (option) => !/\d/.test(option.value)
            )
            stringOptions.sort((a, b) => {
                const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL']
                return sizeOrder.indexOf(a.value) - sizeOrder.indexOf(b.value)
            })
            orderedOptions.push(...stringOptions)

            const numericalOptions = sizeFilter.options.filter((option) =>
                /\d/.test(option.value)
            )
            numericalOptions.sort((a, b) => a.value - b.value)
            orderedOptions.push(...numericalOptions)

            sizeFilter.options = orderedOptions
        }
    },
    initPriceFilter(filter) {
        const minPrice = Number(filter.options[0].value)
        const maxPrice = Number(filter.options[filter.options.length - 1].value)

        this.buffer = Math.ceil(((maxPrice - minPrice) * 0.1 + 1) / 10) * 10

        this.minPriceLimit = minPrice
        this.minPrice = minPrice
        this.maxPriceLimit = maxPrice
        this.maxPrice = maxPrice
        this.minPriceTrigger()
        this.maxPriceTrigger()
    },
    minPriceTrigger() {
        let minPrice = Number(this.minPrice)
        let maxPrice = Number(this.maxPrice)

        if (maxPrice - minPrice < this.buffer) {
            minPrice = maxPrice - this.buffer
            this.minPrice = minPrice
        }

        this.minThumb =
            ((minPrice - this.minPriceLimit) * 100) /
            (this.maxPriceLimit - this.minPriceLimit)

        this.minPriceLabel = formatMoney(minPrice * 100)

        this.updatePriceFilter()
    },
    maxPriceTrigger() {
        let minPrice = Number(this.minPrice)
        let maxPrice = Number(this.maxPrice)

        if (maxPrice - minPrice < this.buffer) {
            maxPrice = minPrice + this.buffer
            this.maxPrice = maxPrice
        }

        this.maxThumb =
            100 -
            ((maxPrice - this.minPriceLimit) * 100) /
                (this.maxPriceLimit - this.minPriceLimit)

        this.maxPriceLabel = formatMoney(this.maxPrice * 100)

        this.updatePriceFilter()
    },
    updatePriceFilter() {
        const priceFilter = this.filters.find(
            (filter) => filter.key === 'price'
        )

        priceFilter.options.forEach((option) => {
            const value = Number(option.value)
            const minPrice = Number(this.minPrice)
            const maxPrice = Number(this.maxPrice)

            option.selected = value >= minPrice && value <= maxPrice
        })
    },
    updateActiveFilters() {
        const activeFilters = this.filters.filter(
            (filter) =>
                filter.key !== 'tags' &&
                filter.key !== 'price' &&
                filter.options.some((option) => option.selected)
        )

        this.activeFilters = activeFilters.reduce((acc, filter) => {
            const selectedOptions = filter.options
                .filter((option) => option.selected)
                .map((option) => {
                    return {
                        filterKey: filter.key,
                        ...option,
                    }
                })

            return [...acc, ...selectedOptions]
        }, [])
    },
    clearFilter(key, value) {
        const filter = this.filters.find((filter) => {
            return filter.key === key
        })

        if (filter) {
            const option = filter.options.find((option) => option.value === value)
            option.selected = false
        }
    },
    clearFilters() {
        this.filters = this.filters.map((filter) => {
            filter.options = filter.options.map((option) => {
                option.selected = false

                return option
            })

            return filter
        })

        this.minPriceTrigger()
        this.maxPriceTrigger()
    },
    transformFilters(filtersData) {
        const filters = filtersData.map((filter) => {
            if (filter.key === 'price') {
                filter.options.sort((a, b) => a.value - b.value)

                filter.options = filter.options.map((option) => {
                    option.name = formatMoney(option.name)
                    return option
                })
            } else {
                filter.options.sort((a, b) => a.name.localeCompare(b.name))
            }

            return filter
        })

        return filters
    },
    handleProductClick(e, product) {
        sendKlevuAnalytics(e, product, '', true)
    },
    updateSelectWidth(el) {
        const width = this.$refs.activeSortLabel.offsetWidth
        const padding = el.computedStyleMap().get('padding-right').value || 0

        if (!width) return

        el.style.width = width + padding + 'px'
    },
    setActiveSortLabel(value) {
        this.activeSortLabel = this.sortOptions.find(
            (option) => option.value === value
        ).label
    },
    toggleColumnSize() {
        this.columnSize = this.columnSize === 2 ? 1 : 2
    },
})
